/**
 * MapLegend Component
 * Comprehensive legend for Pathfinder map showing all active layers
 */

import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Layers as LayersIcon,
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Videocam as CameraGlyphIcon,
  Remove as MinimizeIcon,
} from '@mui/icons-material';
import { useParticipantLabels } from '@signals/app-shell/hooks/useParticipantLabels';
import ReferenceLayersLegend from '@signals/app-shell/components/map/ReferenceLayersLegend';
import type { ReferenceLayerView } from '@signals/app-shell/hooks/useReferenceLayers';
import { useNarrativeStore } from '../../stores/narrativeStore';
import { HudChip, HudDragGlyph, HudStyleButton, useHudDrag, useHudStyle } from './PathfinderHudKit';

const LEGEND_DEFAULT = { bottom: 40, left: 10 };
const LEGEND_Z = 1000;

export type CameraCategoryLegendItem = {
  key: string;
  label: string;
  color: string;
  count: number;
  visible: boolean;
};

const NO_CAMERA_CATEGORIES: CameraCategoryLegendItem[] = [];
const NO_REFERENCE_LAYERS: ReferenceLayerView[] = [];

export interface MapLegendProps {
  // Visibility states for each layer type
  showDataPoints?: boolean;
  showRouteLines?: boolean;
  showDirectionArrows?: boolean;
  showHeatmap?: boolean;
  showDwells?: boolean;
  showCoTravelerPoints?: boolean;
  showCoTravelerLines?: boolean;
  showCoTravelerHeatmap?: boolean;
  showDigitalPoL?: boolean;
  showItinerary?: boolean;
  showCameraLocations?: boolean;
  cameraLocationCount?: number;
  /** Category facets from useCameraLocationsOverlay; hidden ones are listed dimmed. */
  cameraCategories?: CameraCategoryLegendItem[];
  /** Enabled admin reference layers (boundaries, districts, ...), as drawn. */
  referenceLayers?: ReferenceLayerView[];
  showAreaCircles?: boolean;
  
  // Data for dynamic legend items
  studentColors?: Record<string, string>;
  coTravelerCount?: number;
  // List of currently-visible co-travelers with their resolved colors. The
  // legend renders one swatch per entry so users can see which color on the
  // map maps to which co-traveler. Caller is responsible for filtering by
  // visibility and applying user-picked custom colors.
  coTravelerLegendItems?: Array<{
    name: string;
    color: string;
    entityType?: string;
  }>;
  dwellCount?: number;
  areaCircleCount?: number;
  
  // Selected trainee info
  selectedTraineeName?: string;
  selectedTraineeDevices?: Array<{ deviceType: string; color: string }>;
  
  // Uploaded file info
  uploadedFiles?: Array<{ name: string; visible: boolean; pointCount: number }>;
  onToggleUploadedFile?: (name: string, visible: boolean) => void;
  
  // Legend visibility control
  isVisible: boolean;
  onClose: () => void;
  /** True when the caller already filtered items to the current map view. */
  scopedToView?: boolean;
}

// Legend item component for consistent styling
const LegendItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  sublabel?: string;
}> = ({ icon, label, sublabel }) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
      <Box sx={{ 
        width: 24, 
        height: 24, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography 
          variant="caption" 
          sx={{ 
            fontSize: '11px', 
            fontWeight: 500,
            color: 'inherit',
            display: 'block',
            lineHeight: 1.2,
          }}
        >
          {label}
        </Typography>
        {sublabel && (
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '10px', 
              color: 'inherit', opacity: 0.65,
              display: 'block',
              lineHeight: 1.2,
            }}
          >
            {sublabel}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// Section header component (no collapse)
const LegendSection: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography 
        variant="caption" 
        sx={{ 
          fontWeight: 600, 
          fontSize: '10px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          color: 'inherit', opacity: 0.65,
          display: 'block',
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      <Box sx={{ pl: 0.5 }}>
        {children}
      </Box>
    </Box>
  );
};

// Helper to extract student number from device ID
const extractStudentNumber = (deviceId: string): string => {
  // If it's already a student number format like "Student-14" or "SRP-12", return as-is
  if (/^(Student|SRP|RP)-\d+/i.test(deviceId)) {
    // Extract the prefix and number, e.g. "Student-14-phone" -> "Student-14"
    const match = deviceId.match(/^(Student|SRP|RP)-(\d+)/i);
    if (match) {
      return `${match[1]}-${match[2]}`;
    }
  }
  // Otherwise return the original
  return deviceId;
};

// Helper to extract device type from device ID
const extractDeviceType = (deviceId: string): string => {
  const lower = deviceId.toLowerCase();
  if (lower.includes('vehicle')) return 'Vehicle';
  if (lower.includes('phone')) return 'Phone';
  if (lower.includes('watch')) return 'Watch';
  return '';
};

// Helper to format student device label (e.g., "Student-14 Phone")
const formatStudentDeviceLabel = (deviceId: string): string => {
  const studentNum = extractStudentNumber(deviceId);
  const deviceType = extractDeviceType(deviceId);
  if (deviceType) {
    return `${studentNum} ${deviceType}`;
  }
  return studentNum;
};

// Helper to get entity type from device ID
const getEntityType = (deviceId: string): 'SRP' | 'RP' | 'Student' | 'Unknown' => {
  const lower = deviceId.toLowerCase();
  if (lower.startsWith('srp-') || lower.includes('srp')) return 'SRP';
  if (lower.startsWith('rp-') && !lower.startsWith('srp')) return 'RP';
  if (lower.startsWith('student-') || lower.includes('student')) return 'Student';
  return 'Unknown';
};

const MapLegend: React.FC<MapLegendProps> = ({
  showDataPoints = false,
  showRouteLines = false,
  showDirectionArrows = false,
  showHeatmap = false,
  showDwells = false,
  showCoTravelerPoints = false,
  showCoTravelerLines = false,
  showCoTravelerHeatmap = false,
  showDigitalPoL = false,
  showItinerary = false,
  showCameraLocations = false,
  cameraLocationCount = 0,
  cameraCategories = NO_CAMERA_CATEGORIES,
  referenceLayers = NO_REFERENCE_LAYERS,
  showAreaCircles = false,
  studentColors = {} as Record<string, string>,
  coTravelerCount = 0,
  coTravelerLegendItems = [] as Array<{ name: string; color: string; entityType?: string }>,
  dwellCount = 0,
  areaCircleCount = 0,
  selectedTraineeName,
  selectedTraineeDevices = [] as Array<{ deviceType: string; color: string }>,
  uploadedFiles = [] as Array<{ name: string; visible: boolean; pointCount: number }>,
  onToggleUploadedFile,
  isVisible,
  onClose,
  scopedToView = false,
}) => {
  const { plural } = useParticipantLabels();
  // Shares look + drag/minimize behaviour with the Story / Sentinel HUD cards.
  const hud = useHudStyle();
  const drag = useHudDrag('legend', LEGEND_DEFAULT);
  const minimized = useNarrativeStore((s) => s.panelMinimized.legend);
  const setMinimized = useNarrativeStore((s) => s.setPanelMinimized);

  if (!isVisible) return null;

  if (minimized) {
    return (
      <HudChip
        id="legend"
        icon={<LayersIcon sx={{ fontSize: 16 }} />}
        label="Legend"
        tooltip="Show legend"
        defaultSx={LEGEND_DEFAULT}
        zIndex={LEGEND_Z}
        onExpand={() => setMinimized('legend', false)}
      />
    );
  }

  const hudBtnSx = { p: 0.5, color: hud.tokens.muted, '&:hover': { color: hud.accent } };
  
  // Check if any visualization is active
  const hasUploadedFiles = uploadedFiles.length > 0;
  const hasCoTravelerItems = coTravelerLegendItems.length > 0;
  const hasReferenceLayers = referenceLayers.length > 0;
  const hasActiveVisualization =
    showDataPoints || showRouteLines || showHeatmap || showDwells ||
    showCoTravelerPoints || showCoTravelerLines || showCoTravelerHeatmap ||
    hasCoTravelerItems ||
    showDigitalPoL || showItinerary || showCameraLocations || showAreaCircles || hasUploadedFiles ||
    hasReferenceLayers;
  
  // Group devices by entity type and extract unique student numbers
  const deviceEntries = Object.entries(studentColors) as [string, string][];
  
  // Separate SRP/RP (grouped) from Students (individual by device type)
  const srpDevices: { studentNum: string; color: string }[] = [];
  const rpDevices: { studentNum: string; color: string }[] = [];
  const studentDevices: { deviceId: string; label: string; color: string }[] = [];
  
  deviceEntries.forEach(([deviceId, color]) => {
    const entityType = getEntityType(deviceId);
    const studentNum = extractStudentNumber(deviceId);
    
    if (entityType === 'SRP') {
      if (!srpDevices.find(d => d.studentNum === studentNum)) {
        srpDevices.push({ studentNum, color });
      }
    } else if (entityType === 'RP') {
      if (!rpDevices.find(d => d.studentNum === studentNum)) {
        rpDevices.push({ studentNum, color });
      }
    } else if (entityType === 'Student') {
      // For students, keep each device type separate
      const label = formatStudentDeviceLabel(deviceId);
      if (!studentDevices.find(d => d.label === label)) {
        studentDevices.push({ deviceId, label, color });
      }
    }
  });
  
  // Sort student devices by student number then device type
  studentDevices.sort((a, b) => a.label.localeCompare(b.label));
  
  const hasSRP = srpDevices.length > 0;
  const hasRP = rpDevices.length > 0;
  const hasStudents = studentDevices.length > 0;
  
  return (
    <Box
      id="pathfinder-map-legend"
      data-hud-card=""
      sx={{
        ...(drag.positionSx as object),
        ...(hud.surfaceSx as object),
        zIndex: LEGEND_Z,
        fontSize: `${hud.style.fontScale}em`,
        width: 240,
        maxWidth: 'calc(100% - 80px)',
        maxHeight: 'calc(100% - 120px)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        // Items below use theme text colors; force them onto the HUD surface.
        '& .MuiTypography-root': { color: 'inherit' },
      }}
    >
      {/* Header (drag handle) */}
      <Box
        {...drag.handleProps}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.75,
          borderBottom: `1px solid ${hud.tokens.border}`,
          userSelect: 'none',
        }}
      >
        <HudDragGlyph color={hud.tokens.muted} />
        <LayersIcon sx={{ fontSize: 18, color: hud.accent }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '13px', lineHeight: 1.2 }}>
            Map Legend
          </Typography>
          {scopedToView && (
            <Typography variant="caption" sx={{ fontSize: '9.5px', lineHeight: 1.2, display: 'block', opacity: 0.65 }}>
              Items in current view
            </Typography>
          )}
        </Box>
        <HudStyleButton sx={hudBtnSx} showWidth={false} />
        <Tooltip title="Minimize">
          <IconButton size="small" onClick={() => setMinimized('legend', true)} sx={hudBtnSx}>
            <MinimizeIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} sx={hudBtnSx}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
      
      {/* Selected Trainee */}
      {selectedTraineeName && (
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderBottom: `1px solid ${hud.tokens.border}`,
            bgcolor: 'rgba(76, 175, 80, 0.1)',
          }}
        >
          <Typography 
            variant="caption" 
            sx={{ 
              fontWeight: 600, 
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: 'inherit', opacity: 0.65,
              display: 'block',
              mb: 0.5,
            }}
          >
            Selected: {selectedTraineeName}
          </Typography>
          {selectedTraineeDevices.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {selectedTraineeDevices.map((device, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: device.color,
                        border: '1.5px solid white',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                      }}
                    />
                    <Box
                      sx={{
                        width: 14,
                        height: 3,
                        bgcolor: device.color,
                        borderRadius: 1,
                      }}
                    />
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '11px',
                      color: 'inherit',
                    }}
                  >
                    {device.deviceType}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: '#4caf50',
                  border: '1.5px solid white',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                }}
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 600,
                  fontSize: '12px',
                  color: 'inherit',
                }}
              >
                All Devices
              </Typography>
            </Box>
          )}
        </Box>
      )}
      
      {/* Content */}
      <Box
        sx={{
          p: 1.5,
          pt: 1,
          overflowY: 'auto',
          flex: 1,
          minHeight: 0,
        }}
      >
        {scopedToView && (showDataPoints || showRouteLines) && !hasSRP && !hasRP && !hasStudents && (
          <Typography
            variant="caption"
            sx={{ color: 'inherit', opacity: 0.65, fontStyle: 'italic', display: 'block', mb: 1 }}
          >
            No tracks in the current view — pan or zoom out.
          </Typography>
        )}
        {!hasActiveVisualization ? (
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'inherit', opacity: 0.65, 
              fontStyle: 'italic',
              display: 'block',
              textAlign: 'center',
              py: 2,
            }}
          >
            No layers currently visible
          </Typography>
        ) : (
          <>
            {/* SRP - grouped as one entry (red) */}
            {(showDataPoints || showRouteLines) && hasSRP && (
              <LegendItem
                icon={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {showDataPoints && (
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: '#f44336',
                          border: '1.5px solid white',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                        }}
                      />
                    )}
                    {showRouteLines && (
                      <Box
                        sx={{
                          width: 14,
                          height: 3,
                          bgcolor: '#f44336',
                          borderRadius: 1,
                        }}
                      />
                    )}
                  </Box>
                }
                label="SRP"
                sublabel={`${srpDevices.length} ${srpDevices.length === 1 ? 'entity' : 'entities'}`}
              />
            )}
            
            {/* RP - grouped as one entry (orange) */}
            {(showDataPoints || showRouteLines) && hasRP && (
              <LegendItem
                icon={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {showDataPoints && (
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: '#ff9800',
                          border: '1.5px solid white',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                        }}
                      />
                    )}
                    {showRouteLines && (
                      <Box
                        sx={{
                          width: 14,
                          height: 3,
                          bgcolor: '#ff9800',
                          borderRadius: 1,
                        }}
                      />
                    )}
                  </Box>
                }
                label="RP"
                sublabel={`${rpDevices.length} ${rpDevices.length === 1 ? 'entity' : 'entities'}`}
              />
            )}
            
            {/* Students - each device type shown individually with their color */}
            {(showDataPoints || showRouteLines) && hasStudents && (
              <LegendSection title={`${plural} (${studentDevices.length})`}>
                {studentDevices.map(({ deviceId, label, color }) => (
                  <LegendItem
                    key={deviceId}
                    icon={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {showDataPoints && (
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: color,
                              border: '1.5px solid white',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                            }}
                          />
                        )}
                        {showRouteLines && (
                          <Box
                            sx={{
                              width: 14,
                              height: 3,
                              bgcolor: color,
                              borderRadius: 1,
                            }}
                          />
                        )}
                      </Box>
                    }
                    label={label}
                  />
                ))}
              </LegendSection>
            )}
            
            
            {/* Direction Arrows */}
            {showDirectionArrows && showRouteLines && (
              <LegendItem
                icon={
                  <Typography sx={{ fontSize: 14, color: 'inherit' }}>
                    ▲
                  </Typography>
                }
                label="Direction Arrow"
                sublabel="Shows route heading"
              />
            )}
            
            {/* Heatmap */}
            {showHeatmap && (
              <LegendSection title="Heatmap">
                <Box sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      height: 12,
                      background: 'linear-gradient(to right, rgba(0,0,255,0.2), rgba(0,200,255,0.5), rgba(100,255,100,0.7), rgba(255,200,0,0.85), rgba(255,0,0,1))',
                      borderRadius: 0.5,
                      mb: 0.5,
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ fontSize: '9px', color: 'inherit', opacity: 0.65 }}>
                      Low density
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '9px', color: 'inherit', opacity: 0.65 }}>
                      High density
                    </Typography>
                  </Box>
                </Box>
              </LegendSection>
            )}
            
            {/* Dwell Locations */}
            {showDwells && (
              <LegendSection title={`Dwells${dwellCount > 0 ? ` (${dwellCount})` : ''}`}>
                <LegendItem
                  icon={
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255, 0, 0, 0.3)',
                        border: '2px solid #ff0000',
                      }}
                    />
                  }
                  label="Dwell Location"
                  sublabel="Size indicates duration"
                />
              </LegendSection>
            )}
            
            {/* Co-Traveler Analysis */}
            {(showCoTravelerPoints ||
              showCoTravelerLines ||
              showCoTravelerHeatmap ||
              hasCoTravelerItems) && (
              <LegendSection
                title={`Co-Travelers${
                  hasCoTravelerItems
                    ? ` (${coTravelerLegendItems.length}${
                        coTravelerCount > coTravelerLegendItems.length
                          ? `/${coTravelerCount}`
                          : ''
                      })`
                    : coTravelerCount > 0
                    ? ` (${coTravelerCount})`
                    : ''
                }`}
              >
                {/* Per-cotraveler color swatches. These reflect the live
                    colors used on the map, including any user-picked
                    custom colors set via the device-tree color picker. */}
                {coTravelerLegendItems.map((item) => (
                  <LegendItem
                    key={item.name}
                    icon={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: item.color,
                            border: '1.5px solid white',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                          }}
                        />
                        <Box
                          sx={{
                            width: 14,
                            height: 3,
                            bgcolor: item.color,
                            borderRadius: 1,
                          }}
                        />
                      </Box>
                    }
                    label={item.name}
                    sublabel={item.entityType}
                  />
                ))}
                {showCoTravelerPoints && (
                  <LegendItem
                    icon={
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: '#2196f3',
                          border: '1px solid white',
                        }}
                      />
                    }
                    label="Encounter Point"
                    sublabel="Size = confidence"
                  />
                )}
                {showCoTravelerLines && (
                  <LegendItem
                    icon={
                      <Box
                        sx={{
                          width: 20,
                          height: 3,
                          bgcolor: '#2196f3',
                          borderRadius: 1,
                        }}
                      />
                    }
                    label="Connection Line"
                    sublabel="Width = confidence"
                  />
                )}
                {showCoTravelerHeatmap && (
                  <Box sx={{ mt: 0.5 }}>
                    <Box
                      sx={{
                        height: 10,
                        background: 'linear-gradient(to right, rgba(255,0,0,0.1), rgba(255,255,0,0.5), rgba(0,255,0,0.7), rgba(0,255,255,1))',
                        borderRadius: 0.5,
                        mb: 0.25,
                      }}
                    />
                    <Typography variant="caption" sx={{ fontSize: '9px', color: 'inherit', opacity: 0.65 }}>
                      Encounter density
                    </Typography>
                  </Box>
                )}
              </LegendSection>
            )}
            
            {/* Digital PoL */}
            {showDigitalPoL && (
              <LegendSection title="Digital Activity">
                <LegendItem
                  icon={<Box sx={{ width: 12, height: 12, bgcolor: '#3b82f6', borderRadius: '50%' }} />}
                  label="App Info"
                />
                <LegendItem
                  icon={<Box sx={{ width: 12, height: 12, bgcolor: '#8b5cf6', borderRadius: '50%' }} />}
                  label="App Usage"
                />
                <LegendItem
                  icon={<Box sx={{ width: 12, height: 12, bgcolor: '#f59e0b', borderRadius: '50%' }} />}
                  label="Browsing"
                />
                <LegendItem
                  icon={<Box sx={{ width: 12, height: 12, bgcolor: '#10b981', borderRadius: '50%' }} />}
                  label="Calls"
                />
                <LegendItem
                  icon={<Box sx={{ width: 12, height: 12, bgcolor: '#06b6d4', borderRadius: '50%' }} />}
                  label="SMS"
                />
                <LegendItem
                  icon={<Box sx={{ width: 12, height: 12, bgcolor: '#ef4444', borderRadius: '50%' }} />}
                  label="Status"
                />
              </LegendSection>
            )}
            
            {/* Itinerary */}
            {showItinerary && (
              <LegendSection title="Itinerary">
                <LegendItem
                  icon={
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: '#00ff00',
                        border: '2px solid white',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                      }}
                    />
                  }
                  label="Itinerary Entry"
                />
                <LegendItem
                  icon={
                    <Box
                      sx={{
                        width: 20,
                        height: 0,
                        borderTop: '2px dashed #00ff00',
                      }}
                    />
                  }
                  label="Planned Route"
                  sublabel="Connects entries in order"
                />
              </LegendSection>
            )}

            {/* Camera Locations */}
            {showCameraLocations && (
              <LegendSection
                title={`Camera Locations${cameraLocationCount ? ` (${cameraLocationCount})` : ''}`}
              >
                {cameraCategories.length === 0 ? (
                  <LegendItem
                    icon={<CameraGlyphIcon sx={{ fontSize: 16, color: '#00838f', filter: 'drop-shadow(0 0 1px #fff)', opacity: 0.5 }} />}
                    label={scopedToView ? 'No cameras in current view' : 'No cameras near the track'}
                    sublabel={scopedToView ? 'Pan or zoom out, or widen the camera radius' : 'Widen the camera radius in Map Settings'}
                  />
                ) : (
                  cameraCategories.map((cat) => (
                    <Box key={cat.key} sx={{ opacity: cat.visible ? 1 : 0.4 }}>
                      <LegendItem
                        icon={<CameraGlyphIcon sx={{ fontSize: 16, color: cat.color, filter: 'drop-shadow(0 0 1px #fff)' }} />}
                        label={cat.label}
                        sublabel={cat.visible ? `${cat.count.toLocaleString()}` : 'hidden'}
                      />
                    </Box>
                  ))
                )}
              </LegendSection>
            )}
            
            {/* Area Search Circles */}
            {showAreaCircles && areaCircleCount > 0 && (
              <LegendSection title={`Search Areas (${areaCircleCount})`}>
                <LegendItem
                  icon={
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        bgcolor: 'rgba(33, 150, 243, 0.1)',
                        border: '2px solid #2196f3',
                      }}
                    />
                  }
                  label="Search Area"
                  sublabel="No results yet"
                />
                <LegendItem
                  icon={
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255, 152, 0, 0.1)',
                        border: '2px solid #ff9800',
                      }}
                    />
                  }
                  label="Search Area"
                  sublabel="Has results"
                />
              </LegendSection>
            )}

            {/* Admin reference layers (boundaries, districts, infrastructure...) */}
            {hasReferenceLayers && (
              <LegendSection title={`Reference Layers (${referenceLayers.length})`}>
                <ReferenceLayersLegend layers={referenceLayers} />
              </LegendSection>
            )}
            
            {/* Uploaded Data Files */}
            {hasUploadedFiles && (
              <LegendSection title={`Uploaded Data (${uploadedFiles.length})`}>
                {uploadedFiles.map((file) => (
                  <Box
                    key={file.name}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 0.4,
                      cursor: 'pointer',
                      borderRadius: 0.5,
                      px: 0.5,
                      mx: -0.5,
                      opacity: file.visible ? 1 : 0.45,
                      '&:hover': {
                        bgcolor: 'rgba(128,128,128,0.15)',
                      },
                    }}
                    onClick={() => onToggleUploadedFile?.(file.name, !file.visible)}
                  >
                    <UploadIcon sx={{ fontSize: 14, color: file.visible ? '#ff9800' : 'inherit', opacity: file.visible ? 1 : 0.4 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '11px',
                          fontWeight: 500,
                          color: 'inherit', opacity: file.visible ? 1 : 0.5,
                          display: 'block',
                          lineHeight: 1.2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {file.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '10px',
                          color: 'inherit', opacity: 0.65,
                          display: 'block',
                          lineHeight: 1.2,
                        }}
                      >
                        {file.pointCount.toLocaleString()} pts
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: file.visible ? '#4caf50' : 'rgba(128,128,128,0.4)',
                        flexShrink: 0,
                        transition: 'background-color 0.2s',
                      }}
                    />
                  </Box>
                ))}
              </LegendSection>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default MapLegend;
